/**
 * Utility to handle merge tag images in the editor
 * Detects merge tags in image URLs and returns appropriate placeholders for preview mode
 */

/**
 * Pattern to detect merge tags: {{anything}}
 * This matches both regular tags {{tag}} and raw tags {{{{raw}}}}{{tag}}{{{{/raw}}}}
 */
const MERGE_TAG_PATTERN = /{{([^}]+)}}/;

/**
 * Convert merge tag image URLs to visual placeholders in testing mode
 *
 * @param url - The image URL (may contain merge tags)
 * @param mode - The rendering mode ('testing' for editor/preview, 'production' for save/export)
 * @returns The appropriate URL to display
 *
 * @example
 * // In testing mode with merge tag
 * getMergeTagImageUrl('{{listing.images.0}}', 'testing')
 * // Returns: 'https://placehold.co/400x300/e3f2fd/1976d2?text=listing.images.0'
 *
 * @example
 * // In production mode (preserves merge tag)
 * getMergeTagImageUrl('{{listing.images.0}}', 'production')
 * // Returns: '{{listing.images.0}}'
 *
 * @example
 * // Regular URL (no merge tag)
 * getMergeTagImageUrl('https://example.com/image.jpg', 'testing')
 * // Returns: 'https://example.com/image.jpg'
 */
export function getMergeTagImageUrl(url: string, mode: 'testing' | 'production'): string {
  if (!url) {
    return 'https://placehold.co/400x300/e1e1e1/666666?text=No+Image';
  }

  // In production mode, always return the original URL (preserve merge tags)
  if (mode === 'production') {
    return url;
  }

  // In testing mode, check if the URL contains a merge tag
  const match = url.match(MERGE_TAG_PATTERN);

  if (match) {
    // Extract the merge tag name (e.g., "listing.images.0" from "{{listing.images.0}}")
    const tagName = match[1].trim();

    // Remove any raw helper wrappers from the tag name for cleaner display
    const cleanTagName = tagName
      .replace(/{{{{raw}}}}/g, '')
      .replace(/{{{{\/raw}}}}/g, '')
      .replace(/raw/g, '')
      .trim();

    // Return a placeholder image with the merge tag name
    // Blue background (#e3f2fd) with blue text (#1976d2) to indicate dynamic content
    return `https://placehold.co/400x300/e3f2fd/1976d2?text=${encodeURIComponent(cleanTagName)}`;
  }

  // Not a merge tag, return the original URL
  return url;
}

/**
 * Check if a URL contains a merge tag
 *
 * @param url - The URL to check
 * @returns true if the URL contains a merge tag pattern
 */
export function isMergeTagUrl(url: string): boolean {
  if (!url) return false;
  return MERGE_TAG_PATTERN.test(url);
}

/**
 * Process HTML content and replace merge tag image URLs with placeholders
 * This is useful for listing blocks that render raw HTML with img tags
 *
 * @param html - The HTML content to process
 * @param mode - The rendering mode
 * @returns Processed HTML with merge tag images replaced by placeholders
 *
 * @example
 * // In testing mode
 * processHtmlMergeTags('<img src="{{listing.imagePath}}" />', 'testing')
 * // Returns: '<img src="https://placehold.co/400x300/e3f2fd/1976d2?text=listing.imagePath" />'
 */
export function processHtmlMergeTags(html: string, mode: 'testing' | 'production'): string {
  if (!html || mode === 'production') {
    return html;
  }

  // Pattern to match img tags with src attributes containing merge tags
  // Matches: <img src="{{anything}}" or <img src='{{anything}}'
  const imgTagPattern = /<img\s+([^>]*?)src=["'](\{\{[^}]+\}\})["']([^>]*?)>/gi;

  return html.replace(imgTagPattern, (match, before, srcUrl, after) => {
    // Process the merge tag URL to get placeholder
    const processedUrl = getMergeTagImageUrl(srcUrl, mode);

    // Return the img tag with the processed URL
    return `<img ${before}src="${processedUrl}"${after}>`;
  });
}
